/** アカウント突合。名簿(在籍×カタログの希望)と SaaS の実アカウントのずれを並べる。 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  List,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { Link } from "react-router-dom";

import type { ReconcileSaasRow } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { jst } from "../../lib/format";
import { errorText, useInvalidateIdp, useIdpPerms, useReconciliation } from "./hooks";

const { Paragraph, Text } = Typography;

/** 指摘の種類。サーバー(app/idp/constants.py の CAT_*)と同じ日本語をキーにする。 */
const CATEGORIES = [
  { key: "退職・休職者残存", color: "red", note: "在籍していないのにアカウントが生きている" },
  { key: "未発行", color: "orange", note: "在籍していてアカウントが無い" },
  { key: "管理外アカウント", color: "default", note: "名簿に無いアカウント(指摘数には数えない)" },
];

/** メタ情報(mode / status / connector / item_id)は指摘ではない。 */
function emails(row: ReconcileSaasRow, key: string): string[] {
  const v = row[key];
  return Array.isArray(v) ? v.map(String) : [];
}

function SaasCard({ name, row }: { name: string; row: ReconcileSaasRow }) {
  const unavailable = typeof row.status === "string" && row.status;
  const total = CATEGORIES.reduce((n, c) => n + emails(row, c.key).length, 0);

  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <span>{name}</span>
          <Tag>{row.connector || "コネクタなし"}</Tag>
        </Space>
      }
      extra={
        unavailable ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {unavailable}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {total ? `${total} 件` : "ずれなし"}
          </Text>
        )
      }
    >
      {unavailable ? (
        <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
          このカタログ項目は API で照会できない(コネクタ未設定・未実装、または手動運用)。
          手動で発行・停止した結果は突合では見えないので、人の目で確認する。
        </Paragraph>
      ) : total === 0 ? (
        <Text type="secondary">名簿と一致している</Text>
      ) : (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {CATEGORIES.map((c) => {
            const list = emails(row, c.key);
            if (!list.length) return null;
            return (
              <div key={c.key}>
                <Space size={6} style={{ marginBottom: 4 }}>
                  <Tag color={c.color}>{c.key}</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {list.length} 件 / {c.note}
                  </Text>
                </Space>
                <List
                  size="small"
                  bordered
                  dataSource={list}
                  renderItem={(mail) => <List.Item>{mail}</List.Item>}
                />
              </div>
            );
          })}
        </Space>
      )}
    </Card>
  );
}

function Diff({ title, items, color }: { title: string; items: string[]; color: string }) {
  return (
    <Card size="small" title={`${title}(${items.length} 件)`} style={{ flex: "1 1 320px" }}>
      {items.length ? (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          {items.map((k) => (
            <Tag color={color} key={k} style={{ whiteSpace: "normal" }}>
              {k}
            </Tag>
          ))}
        </Space>
      ) : (
        <Text type="secondary">なし</Text>
      )}
    </Card>
  );
}

export function ReconciliationPage() {
  const api = useApi();
  const { canView, canRun } = useIdpPerms();
  const query = useReconciliation(canView);
  const invalidate = useInvalidateIdp();

  const run = useMutation({
    mutationFn: api.runReconciliation,
    onSuccess: (d) => {
      void message.success(
        `突合した(指摘 ${d.findings} 件 / 新規 ${d.new_count} 件 / 解消 ${d.resolved_count} 件)`,
      );
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const d = query.data;
  const saas = Object.entries(d?.report?.saas ?? {});

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          アカウント突合
        </Typography.Title>
        {canRun && (
          <Button type="primary" loading={run.isPending} onClick={() => run.mutate()}>
            いま突合する
          </Button>
        )}
      </Flex>

      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
        在籍者とカタログの希望(<Link to="/hr/catalog">サービスカタログ</Link>
        )から「あるべきアカウント」を作り、コネクタが返す実アカウントと比べる。
        SaaS には触らない(停止も発行もしない)ので、直すのはタスク側の仕事。
      </Paragraph>

      {!canView && (
        <Alert type="info" showIcon message="突合の閲覧には idp:viewer が要る" />
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="突合の結果を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      {canView && !query.isFetching && !d && (
        <Card size="small">
          <Empty
            description={
              canRun
                ? "まだ1度も突合していない。「いま突合する」で実行する"
                : "まだ1度も突合していない"
            }
          />
        </Card>
      )}

      {d && (
        <Space direction="vertical" size="middle" style={{ width: "100%", marginTop: 8 }}>
          <Card size="small">
            <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
              <Descriptions.Item label="実行">{jst(d.started_at)}</Descriptions.Item>
              <Descriptions.Item label="起動">{d.trigger}</Descriptions.Item>
              <Descriptions.Item label="実行者">{d.actor || "—"}</Descriptions.Item>
              <Descriptions.Item label="指摘">
                <Text strong>{d.findings}</Text> 件(新規 {d.new_count} / 解消{" "}
                {d.resolved_count})
              </Descriptions.Item>
            </Descriptions>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {d.first
                ? "初回なので差分は出さない(通知もしない)"
                : "前回と比べて増えた指摘・消えた指摘だけを通知している"}{" "}
              · <Link to="/idp/runs">実行履歴</Link>
            </Text>
          </Card>

          {!d.first && (
            <Flex gap={16} wrap>
              <Diff title="新規" items={d.new} color="red" />
              <Diff title="解消" items={d.resolved} color="green" />
            </Flex>
          )}

          {saas.length ? (
            saas.map(([name, row]) => <SaasCard key={name} name={name} row={row} />)
          ) : (
            <Card size="small">
              <Empty description="有効なカタログ項目が無い(サービスカタログで登録する)" />
            </Card>
          )}
        </Space>
      )}
    </div>
  );
}
